import { Decimal } from 'decimal.js'
import { z } from 'zod'

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20

export const UsernameSchema = z
	.string({ required_error: 'Usuario es requerido' })
	.min(3, { message: 'Usuario es muy corto' })
	.max(20, { message: 'Usuario es muy largo' })
	.regex(/^[a-zA-Z0-9_]+$/, {
		message: 'El usuario solo puede incluir letras, numeros, y guión bajo',
	})
	// users can type the username in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordSchema = z
	.string({ required_error: 'Campo requerido' })
	.min(6, { message: 'La contraseña es muy corta' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	// https://github.com/epicweb-dev/epic-stack/issues/918
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'La contraseña es muy larga',
	})

export const NameSchema = z
	.string({ required_error: 'El nombre es requerido' })
	.min(3, { message: 'El nombre es muy corto' })
	.max(40, { message: 'El nombre es muy largo' })

export const EmailSchema = z
	.string({ required_error: 'Email es requerido' })
	.email({ message: 'Email es inválido' })
	.min(3, { message: 'Email es muy corto' })
	.max(100, { message: 'Email es muy largo' })
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: PasswordSchema, confirmPassword: PasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'Las contraseñas deben coincidir',
			})
		}
	})

export const DefaultStringSchema = z.string({
	required_error: 'Campo requerido',
})

/**
 * Person identification. Always trimmed on the way in: `Person.dni` is what
 * every lookup matches on, so a stray space forks the patient into a second
 * record and their billing accounts stop being found.
 */
export const PersonDniSchema = DefaultStringSchema.trim()

export const DefaultNumberSchema = z
	.number({ required_error: 'Campo requerido' })
	.refine((value) => value >= 0, { message: 'El valor debe ser positivo' })

export const DefaultDecimalSchema = z
	.number({ required_error: 'Campo requerido' })
	.refine((value) => value >= 0, { message: 'El valor debe ser positivo' })
	.refine(
		(value) => {
			const decimal = new Decimal(value)
			return !decimal.isNaN()
		},
		{
			message: 'El valor debe ser un número válido',
		},
	)

export const DefaultPercentageSchema = DefaultDecimalSchema.refine(
	(value) => value <= 100,
	{ message: 'El valor debe ser menor o igual a 100' },
).refine(
	(value) => {
		const decimal = new Decimal(value)
		return !decimal.dividedBy(100).isNaN()
	},
	{
		message: 'El valor debe ser un número válido',
	},
)
